
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { UserCircle, Clock, ShoppingBag, Award, Gift, ChevronLeft, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type CustomerData = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string;
  is_registered: boolean;
};

type CustomerActivity = {
  id: string;
  restaurant_id: string;
  restaurant_name: string | null;
  organisation_id: string;
  organisation_name: string | null;
  total_orders: number;
  total_spend: number;
  first_order_date: string;
  latest_order_date: string;
  favorite_order_type: string | null;
  loyalty_points: number;
  loyalty_tier: string;
};

type CustomerOrder = {
  pumpd_order_id: string;
  restaurant_name: string;
  service_type: string;
  date_and_time: string;
  cart_value: number;
};

export default function CustomerProfile() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [activities, setActivities] = useState<CustomerActivity[]>([]);
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const { toast } = useToast();

  // Function to fetch customer orders with fallback mechanism
  const fetchCustomerOrders = async (customerEmail: string) => {
    setOrdersLoading(true);
    setOrdersError(null);

    try {
      // First attempt - try restaurant_orders_view
      const { data: viewOrdersData, error: viewOrdersError } = await supabase
        .from("restaurant_orders_view")
        .select(`
          pumpd_order_id,
          restaurant_name,
          service_type,
          date_and_time,
          cart_value
        `)
        .eq("customer_email", customerEmail)
        .order("date_and_time", { ascending: false })
        .limit(10);

      if (!viewOrdersError && viewOrdersData) {
        console.log("Successfully fetched orders from restaurant_orders_view");
        setOrders(viewOrdersData);
        setOrdersLoading(false);
        return;
      }

      console.log("Falling back to orders table");
      
      // Fallback - query the orders table directly
      const { data: ordersData, error: ordersError } = await supabase
        .from("orders")
        .select(`
          pumpd_order_id,
          restaurant_name,
          service_type,
          date_and_time,
          cart_value
        `)
        .eq("customer_email", customerEmail)
        .order("date_and_time", { ascending: false })
        .limit(10);

      if (ordersError) {
        throw ordersError;
      }

      setOrders(ordersData || []);
    } catch (err: any) {
      console.error("Error fetching customer orders:", err);
      setOrdersError(err.message || "Failed to load customer orders");
      toast({
        variant: "destructive",
        title: "Error loading orders",
        description: "Could not retrieve customer orders",
      });
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    async function fetchCustomerData() {
      try {
        setLoading(true);
        setError(null);

        if (!customerId) {
          setError("Customer ID is required");
          return;
        }

        // Fetch customer basic info
        const { data: customerData, error: customerError } = await supabase
          .from("customers")
          .select("*")
          .eq("id", customerId)
          .single();

        if (customerError) throw customerError;
        
        // Fetch customer activity across restaurants with restaurant names from restaurants_master
        const { data: activityData, error: activityError } = await supabase
          .from("customer_restaurant_activity")
          .select(`
            id,
            restaurant_id,
            organisation_id,
            total_orders,
            total_spend,
            first_order_date,
            latest_order_date,
            favorite_order_type,
            loyalty_points,
            loyalty_tier
          `)
          .eq("customer_id", customerId);

        if (activityError) throw activityError;

        // Get restaurant and organisation names in a separate query
        const restaurantIds = activityData.map((activity: any) => activity.restaurant_id);
        const { data: restaurantsData, error: restaurantsError } = await supabase
          .from("restaurants_master")
          .select("restaurant_id, restaurant_name, organisation_id, organisation_name")
          .in("restaurant_id", restaurantIds);

        if (restaurantsError) throw restaurantsError;

        // Create a map of restaurant IDs to names
        const restaurantMap = new Map();
        const organisationMap = new Map();
        restaurantsData.forEach((restaurant: any) => {
          restaurantMap.set(restaurant.restaurant_id, restaurant.restaurant_name);
          organisationMap.set(restaurant.organisation_id, restaurant.organisation_name);
        });

        // Add restaurant and organisation names to the activity data
        const processedActivities = activityData.map((activity: any) => ({
          ...activity,
          restaurant_name: restaurantMap.get(activity.restaurant_id) || 'Unknown Restaurant',
          organisation_name: organisationMap.get(activity.organisation_id) || 'Unknown Organisation'
        }));

        setCustomer(customerData);
        setActivities(processedActivities);
        
        // Now fetch orders using our robust method
        await fetchCustomerOrders(customerData.email);

      } catch (err: any) {
        console.error("Error fetching customer data:", err);
        setError(err.message || "Failed to load customer data");
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load customer data",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchCustomerData();
  }, [customerId, toast]);

  // Function to retry loading orders
  const retryLoadingOrders = () => {
    if (customer) {
      fetchCustomerOrders(customer.email);
    }
  };

  // Calculate total stats across all restaurants
  const totalStats = activities.reduce(
    (acc, activity) => {
      acc.totalSpend += activity.total_spend;
      acc.totalOrders += activity.total_orders;
      acc.totalPoints += activity.loyalty_points;
      return acc;
    },
    { totalSpend: 0, totalOrders: 0, totalPoints: 0 }
  );

  // Get the highest loyalty tier
  const highestTier = activities.length
    ? activities.reduce((highest, activity) => {
        // Simple tier comparison - could be more sophisticated
        const tiers = { standard: 1, silver: 2, gold: 3, platinum: 4 };
        const currentTierValue = tiers[activity.loyalty_tier as keyof typeof tiers] || 0;
        const highestTierValue = tiers[highest as keyof typeof tiers] || 0;
        
        return currentTierValue > highestTierValue ? activity.loyalty_tier : highest;
      }, "standard")
    : "standard";

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-5xl">
        <div className="flex items-center gap-4 mb-8">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        {Array(3).fill(0).map((_, i) => (
          <Card key={i} className="mb-6">
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 max-w-5xl">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="container mx-auto p-6 max-w-5xl">
        <Alert>
          <AlertTitle>Not Found</AlertTitle>
          <AlertDescription>Customer not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <Button variant="outline" asChild className="mb-4">
          <Link to="/customers">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Link>
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-6 mb-8">
        {/* Customer Profile Card */}
        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-semibold">Customer Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${customer.first_name} ${customer.last_name}`} />
                <AvatarFallback>
                  <UserCircle className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-lg font-semibold">
                  {customer.first_name} {customer.last_name}
                </h3>
                <p className="text-sm text-muted-foreground">{customer.email}</p>
                {customer.phone && (
                  <p className="text-sm text-muted-foreground">{customer.phone}</p>
                )}
                <div className="mt-2">
                  <Badge variant={customer.is_registered ? "default" : "secondary"}>
                    {customer.is_registered ? "Registered" : "Not Registered"}
                  </Badge>
                  <Badge className="ml-2" variant="outline">
                    {highestTier.charAt(0).toUpperCase() + highestTier.slice(1)} Tier
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-4 flex justify-between text-sm">
            <span>Customer Since: {new Date(customer.created_at).toLocaleDateString()}</span>
            <span>ID: {customer.id.substring(0, 8)}</span>
          </CardFooter>
        </Card>

        {/* Customer Stats Card */}
        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-semibold">Summary Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-muted rounded-lg text-center">
                <ShoppingBag className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-muted-foreground text-sm">Total Orders</p>
                <p className="text-2xl font-bold">{totalStats.totalOrders}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <Award className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-muted-foreground text-sm">Loyalty Points</p>
                <p className="text-2xl font-bold">{totalStats.totalPoints}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <Clock className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-muted-foreground text-sm">Total Spend</p>
                <p className="text-2xl font-bold">${totalStats.totalSpend.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="restaurants" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="restaurants">Restaurants</TabsTrigger>
          <TabsTrigger value="orders">Recent Orders</TabsTrigger>
          <TabsTrigger value="rewards">Available Rewards</TabsTrigger>
        </TabsList>

        {/* Restaurant Activity Tab */}
        <TabsContent value="restaurants">
          <Card>
            <CardHeader>
              <CardTitle>Restaurant Activity</CardTitle>
              <CardDescription>
                Activity across all restaurants and organisations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Restaurant</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Total Spend</TableHead>
                    <TableHead>First Visit</TableHead>
                    <TableHead>Latest Visit</TableHead>
                    <TableHead>Loyalty Points</TableHead>
                    <TableHead>Tier</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activities.length ? (
                    activities.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>{activity.restaurant_name}</TableCell>
                        <TableCell>{activity.organisation_name}</TableCell>
                        <TableCell>{activity.total_orders}</TableCell>
                        <TableCell>${activity.total_spend.toFixed(2)}</TableCell>
                        <TableCell>{new Date(activity.first_order_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(activity.latest_order_date).toLocaleDateString()}</TableCell>
                        <TableCell>{activity.loyalty_points}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {activity.loyalty_tier.charAt(0).toUpperCase() + activity.loyalty_tier.slice(1)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-4">
                        No restaurant activity found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle>Recent Orders</CardTitle>
                <CardDescription>
                  The last 10 orders across all restaurants
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={retryLoadingOrders}
                disabled={ordersLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${ordersLoading ? 'animate-spin' : ''}`} />
                {ordersLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </CardHeader>
            <CardContent>
              {ordersLoading ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center py-10">
                    <div className="text-center">
                      <RefreshCw className="h-10 w-10 mx-auto mb-4 animate-spin text-muted-foreground" />
                      <p>Loading orders...</p>
                    </div>
                  </div>
                </div>
              ) : ordersError ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertTitle>Error loading orders</AlertTitle>
                  <AlertDescription className="flex flex-col gap-2">
                    <p>{ordersError}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={retryLoadingOrders} 
                      className="w-fit"
                    >
                      Try Again
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Restaurant</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.length ? (
                      orders.map((order) => (
                        <TableRow key={order.pumpd_order_id}>
                          <TableCell>{order.pumpd_order_id.substring(0, 8)}</TableCell>
                          <TableCell>{order.restaurant_name}</TableCell>
                          <TableCell>{order.service_type}</TableCell>
                          <TableCell>{new Date(order.date_and_time).toLocaleString()}</TableCell>
                          <TableCell>${order.cart_value.toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-10">
                          <div className="flex flex-col items-center justify-center">
                            <ShoppingBag className="h-10 w-10 text-muted-foreground mb-2" />
                            <p className="text-muted-foreground">No orders found for this customer</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rewards Tab */}
        <TabsContent value="rewards">
          <Card>
            <CardHeader>
              <CardTitle>Available Rewards</CardTitle>
              <CardDescription>
                Rewards and offers available based on loyalty tier
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {totalStats.totalPoints >= 100 ? (
                  <>
                    <RewardCard 
                      title="10% Off Next Order"
                      description="Get 10% off your next order at any participating restaurant"
                      pointsCost={100}
                      currentPoints={totalStats.totalPoints}
                    />
                    <RewardCard 
                      title="Free Appetizer"
                      description="Enjoy a free appetizer with your next meal"
                      pointsCost={150}
                      currentPoints={totalStats.totalPoints}
                    />
                    <RewardCard 
                      title="Free Delivery"
                      description="Free delivery on your next order"
                      pointsCost={75}
                      currentPoints={totalStats.totalPoints}
                    />
                  </>
                ) : (
                  <div className="col-span-full text-center py-8">
                    <Gift className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No rewards available yet</h3>
                    <p className="text-muted-foreground">
                      Earn more points to unlock exciting rewards.
                    </p>
                    <p className="text-sm mt-4">
                      You have <span className="font-bold">{totalStats.totalPoints}</span> points.
                      You need <span className="font-bold">{100 - totalStats.totalPoints}</span> more points to unlock rewards.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Reward Card Component
interface RewardCardProps {
  title: string;
  description: string;
  pointsCost: number;
  currentPoints: number;
}

function RewardCard({ title, description, pointsCost, currentPoints }: RewardCardProps) {
  const canRedeem = currentPoints >= pointsCost;

  return (
    <Card className={`${!canRedeem ? 'opacity-60' : ''} transition-all`}>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-semibold text-lg">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <Gift className="h-8 w-8 text-primary" />
        </div>
        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-sm font-medium">{pointsCost} points</span>
          <Button 
            size="sm" 
            disabled={!canRedeem}
          >
            {canRedeem ? 'Redeem' : `Need ${pointsCost - currentPoints} more`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
